import { Button } from '@/lib';
import { ConfirmationModal } from '@/lib/Modals';
import { SpinnerIcon } from '@/lib/Spinner';
import { FC, PropsWithChildren, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LuPlus, LuTrash } from 'react-icons/lu';
import { useComuniLists } from '../useComuniLists';
import { ImportModeDialog } from './ImportModeDialog';
import { UploadedListsContent } from './UploadedListsContent';
import { useUploadedListsActions } from './useUploadedListsActions';

type ListsTabChromeProps = PropsWithChildren<{
  onUploadClick: () => void;
}>;

const ListsTabChrome: FC<ListsTabChromeProps> = ({ onUploadClick, children }) => {
  const { t } = useTranslation('comuni');

  return (
    <div className='flex flex-col pt-6'>
      <div className='flex items-start justify-between gap-5'>
        <div className='min-w-0'>
          <h3 className='text-2xl font-semibold tracking-tight text-primary-900'>{t('tabs.uploadedLists')}</h3>
          <p className='mt-2 max-w-[820px] text-[14.5px] leading-relaxed text-tertiary-600'>{t('lists.description')}</p>
        </div>
        <Button
          intent='primary'
          size='md'
          onClick={onUploadClick}
          className='flex flex-none items-center gap-2 rounded-[11px] px-6 py-3.5 text-[14.5px] font-bold'
        >
          <LuPlus className='size-3.5' strokeWidth={3} />
          {t('lists.upload')}
        </Button>
      </div>
      {children}
    </div>
  );
};

export const UploadedListsTabContainer: FC = () => {
  const { t } = useTranslation('comuni');
  const { lists, refetch, isLoading, isError } = useComuniLists();
  const { toDelete, setToDelete, chooserOpen, setChooserOpen, isDeleting, handleImportModeSelected, openMapping, handleDelete } =
    useUploadedListsActions(refetch);

  const chrome = (body: ReactNode) => (
    <ListsTabChrome onUploadClick={() => setChooserOpen(true)}>
      {chooserOpen ? <ImportModeDialog onClose={() => setChooserOpen(false)} onSelect={handleImportModeSelected} /> : null}
      {body}
      {toDelete ? (
        <ConfirmationModal
          icon={<LuTrash className='size-12 text-red-600' />}
          onClose={() => !isDeleting && setToDelete(null)}
          onConfirm={handleDelete}
          confirmText='comuni.lists.deleteConfirm'
          isLoading={isDeleting}
          isDismissable={!isDeleting}
          className='bg-red-50'
        >
          <div className='flex flex-col gap-2'>
            <h3 className='text-lg font-semibold text-primary-900'>{t('lists.deleteTitle')}</h3>
            <p className='text-sm text-tertiary-600'>{t('lists.deleteWarning', { name: toDelete.name })}</p>
          </div>
        </ConfirmationModal>
      ) : null}
    </ListsTabChrome>
  );

  if (isLoading) {
    return chrome(
      <div className='mt-16 flex justify-center' role='status' aria-busy='true'>
        <SpinnerIcon className='size-8 text-brand-700' />
      </div>
    );
  }

  if (isError) {
    return chrome(
      <div className='mt-16 flex flex-col items-center gap-3 text-center'>
        <p className='text-sm text-tertiary-600'>{t('lists.loadError')}</p>
        <Button intent='secondary' size='md' text={t('lists.retry')} onClick={refetch} />
      </div>
    );
  }

  return chrome(<UploadedListsContent lists={lists} onOpenMapping={openMapping} onRequestDelete={setToDelete} />);
};

/** Alias kept for ComuniContainer and existing imports. */
export const ListeCaricate = UploadedListsTabContainer;
